import { v4 as uuid4 } from 'uuid'

import { actionLabelMap, IActionSettings, IActionType } from '@makyoapp/makyo-common'

import { LAYOUT, SPECIAL_NODES } from '../constants'
import { WorkflowNode, WorkflowNodeData } from '../types'

export class WorkflowNodeManager {
  /**
   * Creates a new workflow node
   */
  static createNode(
    id: string,
    actionType: IActionType,
    settings: any = {},
    position?: { x: number; y: number },
    label?: string
  ): WorkflowNode {
    return {
      id,
      type: 'workflowNode',
      position: position || { x: 0, y: 0 },
      data: {
        actionValue: {
          id,
          type: actionType,
          settings,
          label: label || actionLabelMap[actionType.toString() as keyof typeof actionLabelMap],
        },
      },
    }
  }

  /**
   * Creates a new node with a generated ID
   */
  static createNodeWithGeneratedId(
    actionType: IActionType,
    settings: any = {},
    position?: { x: number; y: number }
  ): WorkflowNode {
    const id = uuid4()
    return this.createNode(id, actionType, settings, position)
  }

  /**
   * Updates an existing node's data
   */
  static updateNodeData(node: WorkflowNode, actionType: IActionType, settings: IActionSettings = {}): WorkflowNode {
    return {
      ...node,
      data: {
        ...node.data,
        actionValue: {
          ...node.data.actionValue,
          type: actionType,
          settings,
        },
      },
    }
  }

  /**
   * Checks if a node is a conditional node
   */
  static isConditionalNode(node: WorkflowNode): boolean {
    return (
      node.data.actionValue.type === IActionType.ConditionalIf ||
      node.data.actionValue.type === IActionType.ConditionalSwitch ||
      node.data.actionValue.type === IActionType.AddToCart
    )
  }

  /**
   * Checks if a node is a trigger node
   */
  static isTriggerNode(node: WorkflowNode): boolean {
    return node.data.actionValue.id === SPECIAL_NODES.trigger
  }

  /**
   * Gets the position for a new node based on source node
   */
  static getNewNodePosition(sourceNode: WorkflowNode): { x: number; y: number } {
    return {
      x: sourceNode.position.x + LAYOUT.nodeSpacing,
      y: sourceNode.position.y,
    }
  }

  /**
   * Gets the source handle for a specific branch
   */
  static getSourceHandle(nodeId: string, branch: string): string {
    return `${nodeId}-next-action-${branch}`
  }

  /**
   * Extracts branch information from a source handle
   */
  static extractBranchFromSourceHandle(sourceHandle: string): string | null {
    if (sourceHandle.includes('next-action-true')) {
      return 'true'
    } else if (sourceHandle.includes('next-action-false')) {
      return 'false'
    } else if (sourceHandle.includes('next-action-')) {
      const match = sourceHandle.match(/next-action-(.+)/)
      if (match) {
        return match[1]
      }
    }
    return null
  }

  static addActionIdToSwitchActionCase(
    nodes: WorkflowNode[],
    actionIdToUpdate: string,
    caseId: string,
    actionId: string
  ): WorkflowNode[] {
    return nodes.map((node) => {
      if (node.id === actionIdToUpdate) {
        return {
          ...node,
          data: {
            ...node.data,
            actionValue: {
              ...node.data.actionValue,
              settings: {
                ...node.data.actionValue.settings,
                cases: node.data.actionValue.settings?.cases?.map((caseItem: any) => {
                  return {
                    ...caseItem,
                    id: caseItem.id,
                    ...(caseItem?.id === caseId ? { actionId: actionId } : {}),
                  }
                }),
              },
              nextAction: actionId,
            },
          },
        }
      }

      return {
        ...node,
        data: {
          ...node.data,
        },
      }
    })
  }

  static removeActionIdFromSwitchActionCase(
    nodes: WorkflowNode[],
    actionIdToUpdate: string,
    actionIdToRemove: string
  ): WorkflowNode[] {
    return nodes.map((node) => {
      if (node.id === actionIdToUpdate) {
        return {
          ...node,
          data: {
            ...node.data,
            actionValue: {
              ...node.data.actionValue,
              settings: {
                ...node.data.actionValue.settings,
                cases: node.data.actionValue.settings?.cases?.map((caseItem: any) => {
                  const newCase = Object.assign({}, caseItem)
                  if (newCase?.actionId === actionIdToRemove) {
                    delete newCase.actionId
                  }
                  return newCase
                }),
              },
            },
          },
        }
      }
      return {
        ...node,
        data: {
          ...node.data,
        },
      }
    })
  }

  static getNodeLabel(node: WorkflowNodeData, customPluginActions?: any[]): string {
    if (node.actionValue.label) {
      return node.actionValue.label
    }

    const pluginAction = customPluginActions?.find((action) => action.type === node.actionValue.type)
    if (pluginAction) {
      return pluginAction.name || pluginAction.type
    }

    return (
      actionLabelMap[node.actionValue.type.toString() as keyof typeof actionLabelMap] ||
      node.actionValue.type.toString()
    )
  }

  static renameLabel(nodes: WorkflowNode[], nodeId: string, label: string): WorkflowNode[] {
    return nodes.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            actionValue: {
              ...node.data.actionValue,
              label,
            },
          },
        }
      }

      return node
    })
  }

  /**
   * Finds the previous node(s) connected to the current node
   */
  static findPreviousNodes(
    nodes: WorkflowNode[],
    edges: import('../types').WorkflowEdge[],
    nodeId: string
  ): WorkflowNode[] {
    const incomingEdges = edges.filter((edge) => edge.target === nodeId)
    const previousNodes: WorkflowNode[] = []

    incomingEdges.forEach((edge) => {
      const previousNode = nodes.find((node) => node.id === edge.source)
      if (previousNode) {
        previousNodes.push(previousNode)
      }
    })

    return previousNodes
  }

  /**
   * Finds the next node(s) connected from the current node
   */
  static findNextNodes(
    nodes: WorkflowNode[],
    edges: import('../types').WorkflowEdge[],
    nodeId: string
  ): WorkflowNode[] {
    const outgoingEdges = edges.filter((edge) => edge.source === nodeId)
    const nextNodes: WorkflowNode[] = []

    outgoingEdges.forEach((edge) => {
      const nextNode = nodes.find((node) => node.id === edge.target)
      if (nextNode) {
        nextNodes.push(nextNode)
      }
    })

    return nextNodes
  }

  /**
   * Gets comprehensive node context including previous and next nodes
   */
  static getNodeContext(
    nodes: WorkflowNode[],
    edges: import('../types').WorkflowEdge[],
    nodeId: string
  ): {
    currentNode: WorkflowNode | null
    previousNode: WorkflowNode | null
    nextNode: WorkflowNode | null
    previousNodes: WorkflowNode[]
    nextNodes: WorkflowNode[]
    isFirstNode: boolean
    isLastNode: boolean
    isTriggerNode: boolean
  } {
    const currentNode = nodes.find((node) => node.id === nodeId) || null
    const previousNodes = this.findPreviousNodes(nodes, edges, nodeId)
    const nextNodes = this.findNextNodes(nodes, edges, nodeId)

    // Get single previous and next nodes (first one if multiple)
    const previousNode = previousNodes.length > 0 ? previousNodes[0] : null
    const nextNode = nextNodes.length > 0 ? nextNodes[0] : null

    return {
      currentNode,
      previousNode,
      nextNode,
      previousNodes,
      nextNodes,
      isFirstNode: previousNodes.length === 0,
      isLastNode: nextNodes.length === 0,
      isTriggerNode: currentNode ? this.isTriggerNode(currentNode) : false,
    }
  }
}
